import { X, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { EquipmentAutocomplete } from "@/components/project-detail/equipment/EquipmentAutocomplete";
import { useState } from "react";
import type { ProjectEquipmentAssignment } from "@/hooks/useProjectEquipment";

interface AvailableInstance {
  id: string;
  identifier: string;
}

interface CameraEquipmentInlineProps {
  assignment: ProjectEquipmentAssignment | null;
  onAddEquipment: (equipmentId: string) => void;
  onAddTemporary: (name: string) => void;
  onSwapEquipment: (assignmentId: string, newEquipmentId: string) => void;
  onSwapTemporary: (assignmentId: string, tempName: string) => void;
  onRemoveAssignment: (assignmentId: string) => void;
  onAssignInstance: (
    assignmentId: string,
    instanceId: string,
    instancePreview: { id: string; identifier: string; status: string },
  ) => void;
  onUnassignInstance: (instanceAssignmentId: string) => void;
  availableInstances: AvailableInstance[];
  tagIds?: string[];
}

export function CameraEquipmentInline({
  assignment,
  onAddEquipment,
  onAddTemporary,
  onSwapEquipment,
  onSwapTemporary,
  onRemoveAssignment,
  onAssignInstance,
  onUnassignInstance,
  availableInstances,
  tagIds,
}: CameraEquipmentInlineProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [instancePickerOpen, setInstancePickerOpen] = useState(false);

  if (!assignment) {
    // Empty state — show autocomplete to pick equipment
    return (
      <div className="min-w-[120px]">
        {isEditing ? (
          <EquipmentAutocomplete
            value={null}
            onChange={(val) => {
              if (val && !val.isTemporary) {
                onAddEquipment(val.id);
              }
              setIsEditing(false);
            }}
            onCreateTemporary={(name) => {
              onAddTemporary(name);
              setIsEditing(false);
            }}
            placeholder="Add equipment..."
            autoFocus
            tagIds={tagIds}
          />
        ) : (
          <button
            className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded px-1.5 py-0.5"
            onClick={() => setIsEditing(true)}
          >
            —
          </button>
        )}
      </div>
    );
  }

  const name =
    assignment.equipment?.short_name ??
    assignment.equipment?.name ??
    assignment.temporary_equipment?.name ??
    "Unnamed";

  const isSerialized = assignment.equipment?.is_serialized ?? false;
  const currentInstanceAssignment = assignment.instance_assignments?.[0];
  const currentInstanceIdentifier =
    currentInstanceAssignment?.instance?.identifier;

  // Assigned state — show name with swap and remove
  if (isEditing) {
    return (
      <div className="min-w-[160px]">
        <EquipmentAutocomplete
          value={
            assignment.equipment_id
              ? { id: assignment.equipment_id, name, isTemporary: false }
              : assignment.temporary_equipment_id
                ? {
                    id: assignment.temporary_equipment_id,
                    name,
                    isTemporary: true,
                  }
                : null
          }
          onChange={(val) => {
            if (val && !val.isTemporary) {
              onSwapEquipment(assignment.id, val.id);
            }
            setIsEditing(false);
          }}
          onCreateTemporary={(tempName) => {
            onSwapTemporary(assignment.id, tempName);
            setIsEditing(false);
          }}
          placeholder="Swap equipment..."
          autoFocus
          tagIds={tagIds}
        />
      </div>
    );
  }

  const handleInstanceSelect = (instanceId: string) => {
    // If there's already an instance assigned, unassign it first
    if (currentInstanceAssignment) {
      onUnassignInstance(currentInstanceAssignment.id);
    }
    const inst = availableInstances.find((i) => i.id === instanceId);
    onAssignInstance(assignment.id, instanceId, {
      id: instanceId,
      identifier: inst?.identifier ?? "",
      status: "active",
    });
    setInstancePickerOpen(false);
  };

  return (
    <div className="flex items-center gap-1">
      <Badge
        variant="secondary"
        className="gap-0.5 pr-0.5 text-xs font-normal cursor-pointer"
        onClick={() => setIsEditing(true)}
      >
        {name}
        <button
          className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
          onClick={(e) => {
            e.stopPropagation();
            onRemoveAssignment(assignment.id);
          }}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </Badge>

      {isSerialized && (
        <Popover open={instancePickerOpen} onOpenChange={setInstancePickerOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-0.5 text-xs rounded px-1.5 py-0.5 hover:bg-muted text-muted-foreground hover:text-foreground">
              {currentInstanceIdentifier ?? "—"}
              <ChevronDown className="h-2.5 w-2.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search instances..." />
              <CommandList>
                <CommandEmpty>No instances available</CommandEmpty>
                <CommandGroup>
                  {availableInstances.map((inst) => (
                    <CommandItem
                      key={inst.id}
                      value={inst.identifier}
                      onSelect={() => handleInstanceSelect(inst.id)}
                    >
                      {inst.identifier}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
